// SectionRenderer — общий рендерер пользовательского раздела из JSON-модели
// (README §10.3, формат — GET /api/sections/uikit). Цвета, шрифты и отступы
// в JSON не задаются: всё берётся из токенов и UI-кита §10.4 —
// меню трёх уровней (SideMenu), полотно без рамок (рамка только у cards),
// управление — плавающая панель внизу, статусы — цветным текстом.

import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../../api'
import { fmtRate, forecastText, isEnough, leftPercent, useLimits } from '../../lib/limits'
import { useAppStore } from '../../store/appStore'
import type { Section } from '../../store/appStore'
import { FloatButton, FloatingPanel } from '../../ui/FloatingPanel'
import { SideMenu } from '../../ui/SideMenu'
import type { SideItem, SideSection } from '../../ui/SideMenu'
import { applyBuiltinFilter } from './builtinFilter'
import { describe, http, mskHm } from './http'
import { keyOf, useModels } from './model'
import { sectionsApi } from './store'
import type { CanvasBlock, DataSourceDef, SectionAction, SectionModel, SideNode } from './types'
import './custom.css'

type Row = Record<string, unknown>
interface Data { rows: Row[] | null; text: string | null; error: string | null; loading: boolean }

/** Источники Ленты — те же названия и цвета, что в разделе «Лента» (§3). */
const SOURCE_TITLE: Record<string, string> = {
  server: 'Сервер', telegram: 'Телеграм', github: 'GitHub', limits: 'Лимиты', notes: 'Заметки',
}

const EMPTY: Data = { rows: null, text: null, error: null, loading: true }

// ---------------------------------------------------------------------------
// данные источников
// ---------------------------------------------------------------------------

function useSourceData(sectionId: string, source: DataSourceDef | undefined, draft: boolean): Data {
  const [data, setData] = useState<Data>(EMPTY)
  const limits = useLimits()
  const sessions = useAppStore((s) => s.sessions)
  const sig = source ? JSON.stringify(source) : ''
  const kind = source?.kind

  useEffect(() => {
    if (!source || kind === 'limits' || kind === 'sessions') return
    let alive = true
    const load = async () => {
      try {
        if (kind === 'library') {
          const p = source.params ?? {}
          const q = new URLSearchParams()
          if (typeof p.type === 'string') q.set('type', p.type)
          if (typeof p.q === 'string') q.set('q', p.q)
          if (p.installed) q.set('installed', 'true')
          const res = await http<{ items: Row[] }>(`/library?${q}`)
          if (alive) setData({ rows: res.items, text: null, error: null, loading: false })
          return
        }
        const res = await sectionsApi.data(sectionId, source.id, draft)
        if (alive) setData({ rows: res.rows, text: res.text, error: null, loading: false })
      } catch (error) {
        if (alive) setData({ rows: null, text: null, error: describe(error), loading: false })
      }
    }
    void load()
    const every = Math.max(5, source.refresh ?? 0)
    const t = source.refresh ? window.setInterval(() => void load(), every * 1000) : null
    return () => {
      alive = false
      if (t !== null) window.clearInterval(t)
    }
    // sig — сам источник целиком: правка params агентом перечитывает данные
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, sig, draft])

  if (kind === 'limits') {
    return {
      rows: (limits?.providers ?? []).map((p) => ({
        title: p.title, left: leftPercent(p), rate: fmtRate(p.tokensPerMin), forecast: forecastText(p), ok: isEnough(p),
      })),
      text: null, error: null, loading: !limits,
    }
  }
  if (kind === 'sessions') {
    return {
      rows: sessions.map((s) => ({ id: s.id, title: s.title, state: s.state, model: s.model, live: s.state !== 'exited' })),
      text: null, error: null, loading: false,
    }
  }
  return source ? data : { rows: null, text: null, error: null, loading: false }
}

/** Фильтр полотна: «month=09» → строки, где поле month содержит «09». */
function applyFilter(rows: Row[] | null, expr: string | null): Row[] | null {
  if (!rows || !expr) return rows
  const [k, ...rest] = expr.split('=')
  const v = rest.join('=')
  if (!k || !v) return rows
  return rows.filter((r) => String(r[k.trim()] ?? '').toLowerCase().includes(v.trim().toLowerCase()))
}

const str = (v: unknown): string => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v))
const prop = (b: CanvasBlock, k: string): unknown => (b.props ?? {})[k]
const propStr = (b: CanvasBlock, k: string): string | undefined => {
  const v = prop(b, k)
  return typeof v === 'string' ? v : undefined
}

function pick(row: Row, field: string | undefined, fallbacks: string[]): string {
  if (field && row[field] !== undefined) return str(row[field])
  for (const f of fallbacks) if (row[f] !== undefined && row[f] !== null && row[f] !== '') return str(row[f])
  return ''
}

// ---------------------------------------------------------------------------
// действия
// ---------------------------------------------------------------------------

/** Выполнить действие раздела. Действие с `confirm` сначала просит подтверждения
 *  на месте (ConfirmAction в плавающей панели / строке действий оверлея) —
 *  confirm()-диалогов в UI v3 нет; `confirmed` — ответ «выполнить» оттуда. */
export async function runAction(
  sectionKey: string, model: SectionModel, action: SectionAction, confirmed = false,
): Promise<void> {
  const app = useAppStore.getState()
  if (action.confirm && !confirmed) {
    useModels.getState().setAsking(sectionKey, action.id)
    return
  }
  useModels.getState().setAsking(sectionKey, null)
  const { type, value, method } = action.run
  try {
    if (type === 'filter') {
      const cur = useModels.getState().filter[sectionKey]
      const same = cur?.expr === value
      useModels.getState().setFilter(sectionKey, same ? null : action.id, same ? null : value)
      // оверлей встроенного раздела: и естественный фильтр вшитого вида (builtinFilter.ts)
      const [sectionId, kind] = sectionKey.split('|')
      if (kind === 'pub') applyBuiltinFilter(sectionId, same ? null : value)
      return
    }
    if (type === 'prompt') {
      const session = await api.createSession({ type: 'agent', title: `${model.title}: ${action.title}`, prompt: value })
      useAppStore.setState((s) => ({
        sessions: s.sessions.some((x) => x.id === session.id) ? s.sessions : [...s.sessions, session],
        paneIds: s.paneIds.includes(session.id) ? s.paneIds : [...s.paneIds, session.id],
      }))
      app.focusSession(session.id)
      return
    }
    if (type === 'link') {
      if (/^https?:\/\//.test(value)) {
        window.open(value, '_blank', 'noopener')
      } else {
        app.setSection(value.replace(/^\//, '') as Section)
      }
      return
    }
    if (type === 'api') {
      // только API своей панели: /api/... — относительный путь
      const path = value.replace(/^\/api/, '')
      if (!path.startsWith('/') || path.startsWith('//')) throw new Error('действие api: нужен путь панели /api/…')
      await http(path, { method: method ?? 'GET' })
      app.setNotice(`«${action.title}» — готово`)
    }
  } catch (error) {
    app.setNotice(`«${action.title}»: ${describe(error)}`)
  }
}

/** Подтверждение действия на месте: текст `confirm` и «выполнить / отмена». */
export function ConfirmAction({ sectionKey, model, className }: {
  sectionKey: string; model: SectionModel; className?: string
}) {
  const asking = useModels((s) => s.asking[sectionKey] ?? null)
  const action = asking ? (model.actions ?? []).find((a) => a.id === asking) : undefined
  if (!action) return null
  const cancel = () => useModels.getState().setAsking(sectionKey, null)
  return (
    <span className={`abx-sr-confirm${className ? ` ${className}` : ''}`} role="group" aria-label={`подтвердить «${action.title}»`}>
      <span className="abx-sr-confirm__text">{action.confirm}</span>
      <button type="button" className="abx-draftbar__btn is-danger" onClick={() => void runAction(sectionKey, model, action, true)}>
        {action.title}
      </button>
      <button type="button" className="abx-draftbar__btn is-quiet" onClick={cancel}>отмена</button>
    </span>
  )
}

// ---------------------------------------------------------------------------
// боковое меню
// ---------------------------------------------------------------------------

export function SectionSidebar({ sectionId, model, draft }: { sectionId: string; model: SectionModel; draft: boolean }) {
  const key = keyOf(sectionId, draft)
  const filter = useModels((s) => s.filter[key])
  const sources = model.dataSources ?? []
  // источники, из которых меню берёт дочерние пункты — не больше трёх, как уровней меню
  const menuSources = useMemo(() => {
    const ids: string[] = []
    const walk = (nodes: SideNode[]) => nodes.forEach((n) => {
      if (n.dataSource && !ids.includes(n.dataSource)) ids.push(n.dataSource)
      walk(n.children ?? [])
    })
    walk(model.sidebar ?? [])
    return ids.slice(0, 3)
  }, [model.sidebar])
  const d0 = useSourceData(sectionId, sources.find((s) => s.id === menuSources[0]), draft)
  const d1 = useSourceData(sectionId, sources.find((s) => s.id === menuSources[1]), draft)
  const d2 = useSourceData(sectionId, sources.find((s) => s.id === menuSources[2]), draft)
  const byId: Record<string, Data> = {}
  menuSources.forEach((id, i) => { byId[id] = [d0, d1, d2][i] })

  const actions = model.actions ?? []
  const select = (node: SideNode) => {
    const action = node.action ? actions.find((a) => a.id === node.action) : undefined
    if (action) {
      void runAction(key, model, action)
      return
    }
    const same = filter?.node === node.id
    useModels.getState().setFilter(key, same ? null : node.id, same ? null : null)
  }

  const item = (node: SideNode): SideItem => {
    const fromSource = node.dataSource ? byId[node.dataSource]?.rows ?? [] : []
    const kids: SideItem[] = [
      ...(node.children ?? []).map(item),
      ...fromSource.slice(0, 50).map((row, i) => {
        const title = pick(row, undefined, ['title', 'name', 'label', 'path'])
        const id = `${node.id}:${i}`
        return {
          id,
          label: title || '—',
          active: filter?.node === id,
          onSelect: () => {
            const same = filter?.node === id
            useModels.getState().setFilter(key, same ? null : id, same ? null : `title=${title}`)
          },
        }
      }),
    ]
    return {
      id: node.id,
      label: node.title,
      meta: node.count === null || node.count === undefined ? undefined : String(node.count),
      live: node.live,
      active: filter?.node === node.id || (!!node.action && filter?.node === node.action),
      defaultFolded: node.collapsed,
      children: kids.length ? kids : undefined,
      onSelect: kids.length ? undefined : () => select(node),
    }
  }

  const sections: SideSection[] = (model.sidebar ?? []).map((top) => ({
    id: top.id,
    title: top.title,
    count: top.count === null || top.count === undefined ? undefined : String(top.count),
    defaultFolded: top.collapsed,
    items: [
      ...(top.children ?? []).map(item),
      ...(top.dataSource ? item({ ...top, children: [] }).children ?? [] : []),
    ],
  }))
  if (!sections.length) sections.push({ id: 'main', title: model.title, items: [] })
  return <SideMenu screen={`custom-${sectionId}${draft ? '-draft' : ''}`} sections={sections} />
}

// ---------------------------------------------------------------------------
// полотно
// ---------------------------------------------------------------------------

function Block({ sectionId, block, model, draft, expr }: {
  sectionId: string; block: CanvasBlock; model: SectionModel; draft: boolean; expr: string | null
}) {
  const source = block.dataSource ? (model.dataSources ?? []).find((s) => s.id === block.dataSource) : undefined
  const data = useSourceData(sectionId, source, draft)
  const rows = applyFilter(data.rows, expr)
  const title = block.title ? <h2 className="abx-sr__h2">{block.title}</h2> : null

  if (source && data.error) {
    return (
      <div className="abx-sr__block">
        {title}
        <p className="abx-sr__err">{data.error}</p>
      </div>
    )
  }

  switch (block.type) {
    case 'heading': {
      const level = prop(block, 'level') === 2 ? 2 : 1
      const text = propStr(block, 'text') ?? block.title ?? ''
      return level === 1 ? <h1 className="abx-sr__h1">{text}</h1> : <h2 className="abx-sr__h2">{text}</h2>
    }
    case 'text': {
      const text = propStr(block, 'text') ?? data.text ?? (rows ?? []).map((r) => pick(r, propStr(block, 'field'), ['text', 'title'])).join('\n\n')
      return (
        <div className="abx-sr__block">
          {title}
          {text.split(/\n{2,}/).filter(Boolean).map((p, i) => <p key={i} className="abx-sr__p">{p}</p>)}
        </div>
      )
    }
    case 'list': {
      const tf = propStr(block, 'titleField')
      const mf = propStr(block, 'metaField')
      return (
        <div className="abx-sr__block">
          {title}
          <ul className="abx-sr__list">
            {(rows ?? (Array.isArray(prop(block, 'items')) ? (prop(block, 'items') as unknown[]).map((x) => (typeof x === 'object' ? x as Row : { title: x })) : [])).map((r, i) => (
              <li key={i} className="abx-sr__li">
                <span>{pick(r, tf, ['title', 'name', 'label', 'text'])}</span>
                <span className="abx-sr__meta">{pick(r, mf, ['meta', 'status', 'updated', 'time'])}</span>
              </li>
            ))}
          </ul>
          {!data.loading && rows && rows.length === 0 ? <p className="abx-sr__empty">пусто</p> : null}
        </div>
      )
    }
    case 'table': {
      const cols = (Array.isArray(prop(block, 'columns')) ? prop(block, 'columns') as unknown[] : []).map(str)
      const fieldsRaw = Array.isArray(prop(block, 'fields')) ? (prop(block, 'fields') as unknown[]).map(str) : null
      const auto = rows && rows[0] ? Object.keys(rows[0]).slice(0, 6) : []
      const fields = fieldsRaw ?? (cols.length && rows?.[0] && cols.every((c) => c in rows[0]) ? cols : auto)
      const heads = cols.length ? cols : fields
      return (
        <div className="abx-sr__block">
          {title}
          <div className="abx-sr__tablewrap">
            <table className="abx-sr__table">
              <thead><tr>{heads.map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {(rows ?? []).map((r, i) => (
                  <tr key={i}>{(fields.length ? fields : heads).map((f, j) => <td key={j}>{str(r[f])}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          {!data.loading && (!rows || rows.length === 0) ? <p className="abx-sr__empty">пусто</p> : null}
        </div>
      )
    }
    case 'cards': {
      const tf = propStr(block, 'titleField')
      const xf = propStr(block, 'textField')
      const mf = propStr(block, 'metaField')
      return (
        <div className="abx-sr__block">
          {title}
          <div className="abx-sr__cards">
            {(rows ?? []).map((r, i) => (
              <div key={i} className="abx-sr__card">
                <div className="abx-sr__cardtitle">{pick(r, tf, ['title', 'name', 'label'])}</div>
                <div className="abx-sr__cardtext">{pick(r, xf, ['description', 'text', 'detail'])}</div>
                <div className="abx-sr__meta">{pick(r, mf, ['meta', 'source', 'status', 'updated'])}</div>
              </div>
            ))}
          </div>
        </div>
      )
    }
    case 'board': {
      const gf = propStr(block, 'groupField') ?? 'status'
      const groups = new Map<string, Row[]>()
      for (const r of rows ?? []) {
        const g = str(r[gf]) || '—'
        groups.set(g, [...(groups.get(g) ?? []), r])
      }
      return (
        <div className="abx-sr__block">
          {title}
          <div className="abx-sr__board">
            {[...groups.entries()].map(([g, items]) => (
              <div key={g} className="abx-sr__col">
                <div className="abx-sr__colhead">{g} <span className="abx-sr__meta">{items.length}</span></div>
                {items.map((r, i) => (
                  <div key={i} className="abx-sr__card">
                    <div className="abx-sr__cardtitle">{pick(r, propStr(block, 'titleField'), ['title', 'name'])}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )
    }
    case 'feed':
      return (
        <div className="abx-sr__block">
          {title}
          {(rows ?? []).map((r, i) => (
            <p key={i} className="abx-sr__p">
              {r.source ? <b className={`abx-sr__src is-${str(r.source)}`}>{SOURCE_TITLE[str(r.source)] ?? str(r.source)}</b> : null}
              {r.time ? <span className="abx-sr__meta"> · {mskHm(str(r.time))} — </span> : null}
              <span>{pick(r, propStr(block, 'titleField'), ['title', 'name'])}</span>
              {r.detail ? <span className="abx-sr__soft"> {str(r.detail)}</span> : null}
            </p>
          ))}
        </div>
      )
    case 'stat': {
      const field = propStr(block, 'field')
      let value: string = str(prop(block, 'value'))
      if (!value && rows) {
        value = field
          ? String(rows.reduce((sum, r) => sum + (Number(String(r[field] ?? '').replace(/\s/g, '').replace(',', '.')) || 0), 0))
          : String(rows.length)
      }
      return (
        <div className="abx-sr__stat">
          <div className="abx-sr__statnum">{value || '—'}</div>
          {block.title ? <div className="abx-sr__meta">{block.title}</div> : null}
        </div>
      )
    }
    case 'chart': {
      const lf = propStr(block, 'labelField') ?? 'title'
      const vf = propStr(block, 'valueField') ?? 'value'
      const vals = (rows ?? []).map((r) => ({ label: str(r[lf]), value: Number(r[vf]) || 0 }))
      const max = Math.max(1, ...vals.map((v) => v.value))
      return (
        <div className="abx-sr__block">
          {title}
          {vals.map((v, i) => (
            <div key={i} className="abx-sr__bar">
              <span className="abx-sr__barlabel">{v.label}</span>
              <span className="abx-sr__bartrack"><span className="abx-sr__barfill" style={{ width: `${(v.value / max) * 100}%` }} /></span>
              <span className="abx-sr__meta">{v.value}</span>
            </div>
          ))}
        </div>
      )
    }
    case 'embed': {
      const url = propStr(block, 'url')
      return url && /^https?:\/\//.test(url) ? (
        <p className="abx-sr__p"><a className="abx-sr__link" href={url} target="_blank" rel="noreferrer noopener">{block.title ?? url}</a></p>
      ) : null
    }
    default:
      // form и неизвестное: UI-кит — редактирование на месте, без форм; в предпросмотре — подсказка агенту
      return draft ? <p className="abx-sr__err">блок «{block.type}» панель пока не рисует</p> : null
  }
}

export function SectionCanvas({ sectionId, model, draft, panelExtra }: {
  sectionId: string; model: SectionModel; draft: boolean; panelExtra?: ReactNode
}) {
  const key = keyOf(sectionId, draft)
  const filter = useModels((s) => s.filter[key])
  const layout = model.canvas?.layout ?? 'column'
  const maxWidth = model.canvas?.maxWidth ?? (layout === 'article' ? 680 : layout === 'column' ? 720 : 1200)
  const blocks = model.canvas?.blocks ?? []
  const panel = (model.actions ?? []).filter((a) => (a.place ?? 'panel') === 'panel')
  const asking = useModels((s) => s.asking[key] ?? null)
  return (
    <div className="abx-sr">
      <div className={`abx-sr__page is-${layout}`} style={{ maxWidth }}>
        {blocks.length === 0 ? <h1 className="abx-sr__h1">{model.title}</h1> : null}
        {blocks.map((b) => (
          <div key={b.id} className="abx-sr__cell" style={layout === 'grid' || layout === 'split' ? { gridColumn: `span ${Math.min(12, b.span ?? 12)}` } : undefined}>
            <Block sectionId={sectionId} block={b} model={model} draft={draft} expr={filter?.expr ?? null} />
          </div>
        ))}
      </div>
      {asking ? (
        <FloatingPanel bottom={30}>
          <ConfirmAction sectionKey={key} model={model} />
        </FloatingPanel>
      ) : panel.length || panelExtra ? (
        <FloatingPanel bottom={30}>
          {panel.slice(0, 6).map((a) => (
            <FloatButton key={a.id} active={filter?.node === a.id} onClick={() => void runAction(key, model, a)} title={a.run.type === 'prompt' ? 'поручить агенту' : undefined}>
              {a.title}
            </FloatButton>
          ))}
          {panelExtra}
        </FloatingPanel>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// оверлей встроенного раздела
// ---------------------------------------------------------------------------

/** Где блок оверлея стоит относительно вшитого вида: `props.place` — "top" (по
 *  умолчанию) или "bottom". */
export function blockPlace(block: CanvasBlock): 'top' | 'bottom' {
  return prop(block, 'place') === 'bottom' ? 'bottom' : 'top'
}

/** Блоки полотна оверлея над или под вшитым видом встроенного раздела —
 *  тем же рендерером, что и пользовательский раздел; своя прокрутка и потолок
 *  высоты, чтобы вшитый вид остался рабочим. */
export function OverlayBlocks({ sectionId, model, place }: {
  sectionId: string; model: SectionModel; place: 'top' | 'bottom'
}) {
  const key = keyOf(sectionId, false)
  const filter = useModels((s) => s.filter[key])
  const blocks = (model.canvas?.blocks ?? []).filter((b) => blockPlace(b) === place)
  if (!blocks.length) return null
  const layout = model.canvas?.layout ?? 'column'
  const grid = layout === 'grid' || layout === 'split'
  return (
    <div className={`abx-ovl is-${place}`}>
      <div className={`abx-ovl__page is-${grid ? layout : 'column'}`} style={{ maxWidth: model.canvas?.maxWidth ?? undefined }}>
        {blocks.map((b) => (
          <div key={b.id} className="abx-sr__cell" style={grid ? { gridColumn: `span ${Math.min(12, b.span ?? 12)}` } : undefined}>
            <Block sectionId={sectionId} block={b} model={model} draft={false} expr={filter?.expr ?? null} />
          </div>
        ))}
      </div>
    </div>
  )
}

/** Кнопки `actions` (place:"panel") оверлея — строкой в шапке встроенного
 *  раздела: у каждого вшитого вида своя плавающая панель, чужие кнопки в неё
 *  не встраиваются. */
export function OverlayActions({ sectionId, model }: { sectionId: string; model: SectionModel }) {
  const key = keyOf(sectionId, false)
  const filter = useModels((s) => s.filter[key])
  const panel = (model.actions ?? []).filter((a) => (a.place ?? 'panel') === 'panel').slice(0, 6)
  const asking = useModels((s) => s.asking[key] ?? null)
  if (asking) {
    return (
      <div className="abx-ovl-actions">
        <ConfirmAction sectionKey={key} model={model} />
      </div>
    )
  }
  if (!panel.length) return null
  return (
    <div className="abx-ovl-actions">
      {panel.map((a) => (
        <button
          key={a.id}
          type="button"
          className={`abx-draftbar__btn${filter?.node === a.id ? ' is-active' : ''}`}
          onClick={() => void runAction(key, model, a)}
          title={a.run.type === 'prompt' ? 'поручить агенту' : undefined}
        >
          {a.title}
        </button>
      ))}
    </div>
  )
}
